import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MonitorRow } from '@ecinsa/shared';
import { api, type Busqueda, type FichaOrden, type PulsoTaller } from '../lib/api';
import { useTerminal } from '../lib/store';
import { Bala, BarrasDia, BarrasRanking, Grafica, SERIE, SinDatos } from '../components/charts';
import { Dialog, Dot, Empty, LiveTimer, Pill } from '../components/ui';
import { clockTime, hhmm, percent, qty, shortDate } from '../lib/format';

/** Cuántas OFs caben en una pantalla sin que haya que acercarse a leerlas. */
const POR_PAGINA = 6;
/** Cada cuánto pasa de página sola. */
const ROTACION_MS = 20_000;

/**
 * Monitor de fabricación.
 *
 * Está pensado para una pantalla colgada en el taller, a tres metros y sin
 * teclado: nadie va a pasar el ratón por encima ni a pulsar nada. De ahí tres
 * decisiones que lo separan del panel de dirección:
 *
 *  · Todo dato importante va escrito al lado de su barra. Lo que solo se ve al
 *    pasar por encima, en una pantalla colgada no se ve nunca.
 *  · La lista de OFs pasa de página sola, para que a lo largo del turno acaben
 *    saliendo todas sin que nadie toque nada.
 *  · Se actualiza al instante por WebSocket y, si la conexión se cae, un
 *    temporizador de respaldo sigue refrescando; y si aun así deja de llegar
 *    nada, lo dice. Una pantalla de taller congelada enseñando datos viejos es
 *    peor que una apagada: nadie sabe que ha dejado de contar.
 */
export function Monitor() {
  const { catalog } = useTerminal();
  const navigate = useNavigate();
  const [rows, setRows] = useState<MonitorRow[]>([]);
  const [pulso, setPulso] = useState<PulsoTaller | null>(null);
  const [outbox, setOutbox] = useState<Record<string, number>>({});
  const [sapError, setSapError] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [pagina, setPagina] = useState(0);
  const [reloj, setReloj] = useState(new Date());
  const [abierta, setAbierta] = useState<number | null>(null);
  const [consulta, setConsulta] = useState('');
  const [resultados, setResultados] = useState<Busqueda | null>(null);
  // Cuando alguien toca la pantalla, el carrusel se para: si no, le cambia la
  // página debajo mientras está leyendo.
  const [pausaHasta, setPausaHasta] = useState(0);

  const refreshSeconds = Number(catalog?.parameters['monitor.refreshSec'] || 30);

  const load = useCallback(async () => {
    try {
      const [board, latido] = await Promise.all([api.board(), api.pulso()]);
      setRows(board.rows);
      setOutbox(board.outbox);
      setSapError(board.sapError);
      setPulso(latido);
      setUpdatedAt(new Date());
    } catch {
      // Sin datos nuevos se mantiene lo último bueno en pantalla, y el reloj de
      // «actualizado a las…» deja de avanzar: eso es lo que avisa.
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), Math.max(5, refreshSeconds) * 1000);
    return () => clearInterval(timer);
  }, [load, refreshSeconds]);

  useEffect(() => {
    const t = setInterval(() => setReloj(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // WebSocket: los cambios llegan al instante; si se cae, el temporizador de
  // arriba sigue refrescando.
  useEffect(() => {
    const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
    let socket: WebSocket | null = null;
    let retry: number | undefined;

    const connect = () => {
      socket = new WebSocket(`${protocol}://${location.host}/api/monitor/ws`);
      socket.onopen = () => setLive(true);
      socket.onmessage = () => void load();
      socket.onclose = () => {
        setLive(false);
        retry = window.setTimeout(connect, 5000);
      };
      socket.onerror = () => socket?.close();
    };
    connect();

    return () => {
      window.clearTimeout(retry);
      socket?.close();
    };
  }, [load]);

  /**
   * Las que importan, primero.
   *
   * Lo que está en marcha ahora; después lo que tiene una incidencia abierta;
   * después lo que va tarde; y el resto por fecha de entrega. Una pantalla de
   * taller ordenada por número de OF no dice nada: el número no es una
   * prioridad.
   */
  const ordenadas = useMemo(() => {
    const peso = (r: MonitorRow) =>
      r.phases.some((f) => f.state === 'running') ? 0 : r.incident ? 1 : r.delivery === 'late' ? 2 : 3;
    return [...rows]
      .filter((r) => r.status === 'released')
      .sort((a, b) => peso(a) - peso(b) || (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'));
  }, [rows]);

  const buscando = consulta.trim().length >= 2;

  /*
    Se espera a que pare de teclear antes de preguntar. Sin esta espera, cada
    letra lanza una consulta contra miles de OFs y las respuestas llegan
    desordenadas: se acaba viendo el resultado de «26» después del de «26400».
  */
  useEffect(() => {
    if (!buscando) {
      setResultados(null);
      return;
    }
    const t = setTimeout(() => {
      void api
        .buscar(consulta.trim())
        .then((r) => setResultados(r.resultados))
        .catch(() => setResultados(null));
    }, 250);
    return () => clearTimeout(t);
  }, [consulta, buscando]);

  const paginas = Math.max(1, Math.ceil(ordenadas.length / POR_PAGINA));
  // Buscando no se pasa de página sola: lo que se está leyendo desaparecería.
  const pausado = reloj.getTime() < pausaHasta || abierta !== null || buscando;

  useEffect(() => {
    if (paginas <= 1 || pausado) return;
    const t = setInterval(() => setPagina((p) => (p + 1) % paginas), ROTACION_MS);
    return () => clearInterval(t);
  }, [paginas, pausado]);

  /** Pasar de página a mano para y reanuda el carrusel pasados dos minutos. */
  const irA = (siguiente: number) => {
    setPagina(((siguiente % paginas) + paginas) % paginas);
    setPausaHasta(Date.now() + 120_000);
  };

  const visibles = ordenadas.slice(pagina * POR_PAGINA, pagina * POR_PAGINA + POR_PAGINA);

  const enMarcha = ordenadas.filter((r) => r.phases.some((f) => f.state === 'running')).length;
  const tarde = ordenadas.filter((r) => r.delivery === 'late').length;
  const incidencias = ordenadas.filter((r) => r.incident).length;
  const piezasHoy = pulso?.horas.reduce((s, h) => s + h.piezas, 0) ?? 0;

  const barrasHoras = useMemo(
    () =>
      (pulso?.horas ?? []).map((h) => ({
        etiqueta: h.hora,
        eje: h.hora,
        segmentos: [
          { clave: 'Trabajando', valor: h.run, color: SERIE.produciendo },
          { clave: 'Parado', valor: h.stop, color: SERIE.parado },
        ],
      })),
    [pulso],
  );

  const barrasParadas = useMemo(
    () =>
      (pulso?.paradas ?? []).map((p) => ({
        etiqueta: `${p.name} · ${p.veces} ${p.veces === 1 ? 'vez' : 'veces'}`,
        segmentos: [{ clave: 'Tiempo parado', valor: p.seconds, color: SERIE.parado }],
      })),
    [pulso],
  );

  const barrasFases = useMemo(
    () =>
      (pulso?.fases ?? []).map((f) => ({
        etiqueta: `${f.fase} · ${f.operarios} ${f.operarios === 1 ? 'persona' : 'personas'}`,
        segmentos: [{ clave: 'Tiempo imputado', valor: f.seconds, color: SERIE.produciendo }],
      })),
    [pulso],
  );

  // Unos datos de hace veinte minutos presentados como actuales engañan más que
  // una pantalla en blanco, así que se dice.
  const segundosSinActualizar = updatedAt ? Math.round((reloj.getTime() - updatedAt.getTime()) / 1000) : null;
  const obsoleto = segundosSinActualizar !== null && segundosSinActualizar > Math.max(60, refreshSeconds * 3);

  return (
    <div className="app app--escritorio monitor">
      <header className="topbar">
        <button className="btn btn--ghost" onClick={() => navigate('/')}>
          ←
        </button>
        <div>
          <div className="topbar__title">Monitor de fabricación</div>
          <div className="topbar__sub">
            {updatedAt ? `Actualizado a las ${clockTime(updatedAt)}` : 'Cargando…'}
            {paginas > 1 ? ` · página ${pagina + 1} de ${paginas}${pausado ? ' · en pausa' : ''}` : ''}
          </div>
        </div>
        {paginas > 1 && !buscando ? (
          <div className="row" style={{ gap: 6 }}>
            <button className="btn btn--ghost" onClick={() => irA(pagina - 1)} aria-label="Página anterior">‹</button>
            <button className="btn btn--ghost" onClick={() => irA(pagina + 1)} aria-label="Página siguiente">›</button>
          </div>
        ) : null}
        <button className="btn btn--ghost" onClick={() => navigate('/monitor/of')}>
          Quién está en cada OF
        </button>
        <div className="monitor__buscador">
          <input
            className="input"
            value={consulta}
            onChange={(e) => setConsulta(e.target.value)}
            placeholder="Buscar OF, artículo, persona, fase, lote…"
            aria-label="Buscar"
          />
          {buscando ? (
            <button className="btn btn--ghost" onClick={() => setConsulta('')} aria-label="Limpiar búsqueda">
              ✕
            </button>
          ) : null}
        </div>
        <div className="topbar__spacer" />
        {obsoleto ? (
          <Pill tone="danger">Sin datos nuevos desde hace {Math.round((segundosSinActualizar ?? 0) / 60)} min</Pill>
        ) : null}
        {sapError ? <Pill tone="danger">SAP: {sapError.slice(0, 50)}</Pill> : null}
        {outbox.failed ? <Pill tone="danger">{outbox.failed} envíos con error</Pill> : null}
        {outbox.pending ? <Pill tone="warn">{outbox.pending} en cola</Pill> : null}
        <div className="monitor__reloj mono">{clockTime(reloj)}</div>
        <div className="conn">
          <Dot tone={live && !obsoleto ? 'live' : 'warn'} />
          {live ? 'En vivo' : 'Reconectando'}
        </div>
      </header>

      <div className="content stack">
        <div className="kpi kpi--monitor">
          <Cifra
            etiqueta="Trabajando ahora"
            valor={String(pulso?.trabajando.length ?? 0)}
            sub={`en ${enMarcha} ${enMarcha === 1 ? 'orden' : 'órdenes'}`}
            tono={(pulso?.trabajando.length ?? 0) > 0 ? 'ok' : undefined}
          />
          <Cifra etiqueta="Piezas hoy" valor={qty(piezasHoy)} sub="recibidas de producción" />
          <Cifra
            etiqueta="OFs liberadas"
            valor={String(ordenadas.length)}
            sub={`${tarde} fuera de fecha`}
            tono={tarde > 0 ? 'warn' : undefined}
          />
          <Cifra
            etiqueta="Incidencias"
            valor={String(incidencias)}
            sub={incidencias > 0 ? 'bloquean finalizar fase' : 'ninguna abierta'}
            tono={incidencias > 0 ? 'danger' : 'ok'}
          />
          <Cifra
            etiqueta="OEE 24 h"
            valor={pulso ? percent(pulso.oee.oee) : '—'}
            sub={pulso ? `disponibilidad ${percent(pulso.oee.availability)} · calidad ${percent(pulso.oee.quality)}` : ''}
            proporcion={pulso?.oee.oee}
          />
        </div>

        <div className="monitor__columnas">
          <section className="stack">
            {buscando ? (
              <Resultados
                consulta={consulta}
                datos={resultados}
                onAbrirOrden={(docEntry) => setAbierta(docEntry)}
              />
            ) : (
              <>
                <div className="eyebrow">Órdenes en fabricación</div>
                {visibles.length === 0 ? (
                  <SinDatos texto="No hay ninguna orden liberada en SAP." />
                ) : (
                  visibles.map((r) => (
                    <TarjetaOrden key={r.docEntry} orden={r} onAbrir={() => setAbierta(r.docEntry)} />
                  ))
                )}
              </>
            )}
          </section>

          <section className="stack">
            <Grafica titulo="Quién está trabajando" sub="Con el cronómetro corriendo ahora mismo">
              {!pulso || pulso.trabajando.length === 0 ? (
                <SinDatos texto="Nadie tiene una fase en marcha en este momento." />
              ) : (
                <div className="stack" style={{ gap: 10 }}>
                  {pulso.trabajando.map((t) => (
                    <div key={t.employeeId} className="row monitor__persona">
                      <Dot tone="live" />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="monitor__persona-nombre">{t.nombre}</div>
                        <div className="small muted">
                          OF {t.docNum ?? '—'} · {t.fase ?? 'sin fase'}
                        </div>
                      </div>
                      <LiveTimer startedAt={t.desde} className="mono monitor__cronometro" />
                    </div>
                  ))}
                </div>
              )}
            </Grafica>

            <Grafica titulo="Hoy, hora a hora" sub="Tiempo imputado en las últimas 12 horas">
              <BarrasDia
                barras={barrasHoras}
                series={[
                  { clave: 'Trabajando', color: SERIE.produciendo },
                  { clave: 'Parado', color: SERIE.parado },
                ]}
                formato={hhmm}
                altura={150}
              />
            </Grafica>

            <Grafica titulo="Dónde se para" sub="Paradas con causa registradas hoy">
              <BarrasRanking
                barras={barrasParadas}
                series={[{ clave: 'Tiempo parado', color: SERIE.parado }]}
                formato={hhmm}
                vacio="Hoy no se ha registrado ninguna parada con causa."
              />
            </Grafica>

            <Grafica titulo="Carga por fase" sub="Tiempo imputado hoy en cada fase">
              <BarrasRanking
                barras={barrasFases}
                series={[{ clave: 'Tiempo imputado', color: SERIE.produciendo }]}
                formato={hhmm}
                vacio="Todavía no se ha imputado tiempo hoy."
              />
            </Grafica>
          </section>
        </div>
      </div>

      {abierta !== null ? <Ficha docEntry={abierta} onClose={() => setAbierta(null)} /> : null}
    </div>
  );
}

/**
 * Una orden, de un vistazo.
 *
 * El orden de lectura es el de las preguntas que se hacen en el taller: qué se
 * fabrica, cuánto llevamos, cuánto tiempo frente a lo previsto, en qué fase va
 * y quién está en ella.
 */
function TarjetaOrden({ orden, onAbrir }: { orden: MonitorRow; onAbrir: () => void }) {
  const avance = orden.plannedQuantity > 0 ? orden.completedQuantity / orden.plannedQuantity : 0;
  const activa = orden.phases.some((f) => f.state === 'running');
  // Si la ruta de la OF no tiene tiempos creíbles, tampoco se enseña el
  // previsto de cada fase: repetir «/0 h 03 min» en cada etiqueta llena la
  // pantalla de un dato que ya hemos dicho que no sirve.
  const rutaFiable = orden.plannedSeconds > 0 && orden.laborSeconds <= orden.plannedSeconds * 10;

  return (
    <article
      className={`card monitor__of monitor__of--pulsable${activa ? ' monitor__of--activa' : ''}`}
      role="button"
      tabIndex={0}
      onClick={onAbrir}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onAbrir();
        }
      }}
    >
      <div className="row row--wrap" style={{ alignItems: 'flex-start', gap: 12 }}>
        <Dot
          tone={
            orden.incident ? 'danger'
            : activa ? 'live'
            : orden.delivery === 'late' ? 'danger'
            : orden.delivery === 'soon' ? 'warn'
            : 'muted'
          }
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="monitor__of-titulo">
            OF {orden.docNum}
            <span className="monitor__of-articulo"> · {orden.itemDescription}</span>
          </div>
          <div className="small muted">
            {orden.itemCode}
            {orden.customerName ? ` · ${orden.customerName}` : ''}
            {orden.dueDate ? ` · entrega ${shortDate(orden.dueDate)}` : ''}
          </div>
        </div>
        {orden.incident ? <Pill tone="danger">⚠ {orden.incident.name}</Pill> : null}
        <div className="monitor__of-cantidad mono">
          {qty(orden.completedQuantity)}
          <span className="muted">/{qty(orden.plannedQuantity)}</span>
          {orden.rejectedQuantity > 0 ? (
            <div className="small" style={{ color: 'var(--danger)' }}>{qty(orden.rejectedQuantity)} rechazadas</div>
          ) : null}
        </div>
      </div>

      <div className="monitor__of-barras">
        <div>
          <div className="small muted" style={{ marginBottom: 4 }}>Piezas</div>
          <div className="bar">
            <div className="bar__fill" style={{ width: `${Math.min(100, avance * 100)}%` }} />
          </div>
          <div className="small mono muted" style={{ marginTop: 4 }}>{percent(avance)} del pedido</div>
        </div>
        <div>
          <div className="small muted" style={{ marginBottom: 4 }}>Tiempo</div>
          <Bala
            consumido={orden.laborSeconds}
            parado={orden.stopSeconds}
            previsto={orden.plannedSeconds}
            formato={hhmm}
          />
        </div>
      </div>

      <div className="row row--wrap" style={{ gap: 6 }}>
        {orden.phases.map((f) => (
          <span
            key={f.code}
            className={`pill${
              f.state === 'running' ? ' pill--ok'
              : f.state === 'done' ? ' pill--accent'
              : f.state === 'paused' ? ' pill--warn'
              : ''
            }`}
          >
            {f.state === 'running' ? '▶ ' : f.state === 'done' ? '✓ ' : ''}
            {f.code}
            {f.seconds > 0 ? <span className="mono"> · {hhmm(f.seconds)}</span> : null}
            {rutaFiable && f.plannedSeconds > 0 ? <span className="muted"> /{hhmm(f.plannedSeconds)}</span> : null}
            {f.operators.length ? <strong> · {f.operators.join(', ')}</strong> : null}
          </span>
        ))}
      </div>
    </article>
  );
}

/** Un número grande con su contexto debajo. No es una gráfica y no lo finge. */
function Cifra({
  etiqueta,
  valor,
  sub,
  tono,
  proporcion,
}: {
  etiqueta: string;
  valor: string;
  sub?: string;
  tono?: 'ok' | 'warn' | 'danger';
  proporcion?: number;
}) {
  const color =
    tono === 'ok' ? 'var(--ok)' : tono === 'warn' ? 'var(--warn)' : tono === 'danger' ? 'var(--danger)' : undefined;
  return (
    <div className="kpi__card">
      <div className="kpi__label">{etiqueta}</div>
      <div className="kpi__value monitor__cifra" style={color ? { color } : undefined}>{valor}</div>
      {proporcion !== undefined ? (
        <div className="bar">
          <div
            className="bar__fill"
            style={{
              width: `${Math.round(Math.max(0, Math.min(1, proporcion)) * 100)}%`,
              background: color ?? SERIE.produciendo,
            }}
          />
        </div>
      ) : null}
      {sub ? <div className="small muted" style={{ marginTop: 6 }}>{sub}</div> : null}
    </div>
  );
}

/**
 * Ficha de la orden: todo lo que se sabe de ella, en una pantalla.
 *
 * SAP sabe que se emitieron cuarenta minutos de un recurso. No sabe que los
 * hizo Marta el martes por la tarde, que hubo una avería de quince minutos ni
 * de qué lote salió el material. Eso solo está aquí, y hasta ahora no había
 * dónde verlo: es el motivo por el que esta ficha existe.
 */
function Ficha({ docEntry, onClose }: { docEntry: number; onClose: () => void }) {
  const [datos, setDatos] = useState<FichaOrden | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pestana, setPestana] = useState<'resumen' | 'imputaciones' | 'componentes' | 'sap'>('resumen');

  useEffect(() => {
    let vivo = true;
    const cargar = () =>
      api
        .ficha(docEntry)
        .then((d) => vivo && setDatos(d))
        .catch((e: Error) => vivo && setError(e.message));
    void cargar();
    // Mientras está abierta sigue viva: si alguien arranca una fase, se ve.
    const t = setInterval(cargar, 15_000);
    return () => {
      vivo = false;
      clearInterval(t);
    };
  }, [docEntry]);

  if (error) {
    return (
      <Dialog title="No se ha podido abrir la orden" onClose={onClose}>
        <div className="toast toast--error" style={{ boxShadow: 'none' }}>{error}</div>
      </Dialog>
    );
  }
  if (!datos) {
    return (
      <Dialog title="Abriendo la orden…" onClose={onClose} wide>
        <Empty icon="⏳" title="Leyendo todo lo que hay de esta OF" />
      </Dialog>
    );
  }

  const o = datos.orden;
  const componentes = o.lines.filter((l) => l.type === 'item');
  const recursos = o.lines.filter((l) => l.type === 'resource');
  const totalImputado = datos.fases.reduce((s, f) => s + f.segundos, 0);
  const totalPrevisto = datos.fases.reduce((s, f) => s + f.previstoSegundos, 0);
  const totalParado = datos.fases.reduce((s, f) => s + f.parado, 0);

  return (
    <Dialog
      title={`OF ${o.docNum} · ${o.itemDescription}`}
      subtitle={`${o.itemCode} · almacén ${o.warehouse}${o.customerName ? ` · ${o.customerName}` : ''}`}
      onClose={onClose}
      wide
    >
      <div className="stack">
        <div className="kpi">
          <DatoFicha etiqueta="Estado" valor={estadoLegible(o.status)} />
          <DatoFicha etiqueta="Piezas" valor={`${qty(o.completedQuantity)} / ${qty(o.plannedQuantity)}`} sub={o.rejectedQuantity > 0 ? `${qty(o.rejectedQuantity)} rechazadas` : 'sin rechazos'} />
          <DatoFicha etiqueta="Tiempo imputado" valor={hhmm(totalImputado)} sub={totalParado > 0 ? `${hhmm(totalParado)} parado` : 'sin paradas'} />
          <DatoFicha etiqueta="Previsto por la ruta" valor={totalPrevisto > 0 ? hhmm(totalPrevisto) : 'sin tiempos'} />
          <DatoFicha etiqueta="Fechas" valor={shortDate(o.dueDate)} sub={`inicio ${shortDate(o.startDate)}`} />
        </div>

        <div className="chips">
          {([
            ['resumen', `Fases (${datos.fases.length})`],
            ['imputaciones', `Imputaciones (${datos.imputaciones.length})`],
            ['componentes', `Materiales (${componentes.length})`],
            ['sap', `Documentos (${datos.documentos.length + datos.recibos.length})`],
          ] as const).map(([k, l]) => (
            <button key={k} className={`chip${pestana === k ? ' chip--selected' : ''}`} onClick={() => setPestana(k)}>
              {l}
            </button>
          ))}
        </div>

        {pestana === 'resumen' ? (
          <div style={{ overflowX: 'auto' }}>
            <table className="table table--zebra">
              <thead>
                <tr><th>Fase</th><th>Estado</th><th>Imputado</th><th>Previsto</th><th>Parado</th><th>Quién ha trabajado</th></tr>
              </thead>
              <tbody>
                {datos.fases.map((f) => (
                  <tr key={f.code}>
                    <td><strong>{f.code}</strong></td>
                    <td>{f.enMarcha ? <Pill tone="ok">En marcha</Pill> : f.segundos > 0 ? <Pill>Con tiempo</Pill> : <span className="muted small">Sin empezar</span>}</td>
                    <td className="mono">{hhmm(f.segundos)}</td>
                    <td className="mono muted">{f.previstoSegundos > 0 ? hhmm(f.previstoSegundos) : '—'}</td>
                    <td className="mono">{f.parado > 0 ? <span style={{ color: 'var(--warn)' }}>{hhmm(f.parado)}</span> : '—'}</td>
                    <td className="small">{f.operarios.join(', ') || <span className="muted">nadie todavía</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {datos.fasesFinalizadas.length > 0 ? (
              <>
                <div className="eyebrow" style={{ marginTop: 18 }}>Fases finalizadas</div>
                <table className="table table--zebra">
                  <thead><tr><th>Fase</th><th>Quién</th><th>Piezas</th><th>Rechazo</th><th>Cuándo</th><th>SAP</th></tr></thead>
                  <tbody>
                    {datos.fasesFinalizadas.map((f, i) => (
                      <tr key={`${f.fase}-${i}`}>
                        <td>{f.fase}</td>
                        <td>{f.por ?? '—'}</td>
                        <td className="mono">{qty(f.cantidad)}</td>
                        <td className="mono">{f.rechazo > 0 ? `${qty(f.rechazo)}${f.causaRechazo ? ` (${f.causaRechazo})` : ''}` : '—'}</td>
                        <td className="small">{new Date(f.cuando).toLocaleString('es-ES')}</td>
                        <td>{f.enviadoASap ? <Pill tone="ok">Enviado</Pill> : <Pill tone="warn">Solo aquí</Pill>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : null}
          </div>
        ) : null}

        {pestana === 'imputaciones' ? (
          <div style={{ overflowX: 'auto' }}>
            {datos.imputaciones.length === 0 ? (
              <SinDatos texto="Nadie ha imputado tiempo todavía en esta orden." />
            ) : (
              <table className="table table--zebra">
                <thead>
                  <tr><th>Operario</th><th>Fase</th><th>Recurso</th><th>Desde</th><th>Hasta</th><th>Tiempo</th><th>Causa</th><th>Origen</th></tr>
                </thead>
                <tbody>
                  {datos.imputaciones.map((t) => (
                    <tr key={t.id}>
                      <td>{t.nombre}</td>
                      <td className="small">{t.fase}</td>
                      <td className="small muted mono">{t.recurso ?? '—'}</td>
                      <td className="small">{new Date(t.desde).toLocaleString('es-ES')}</td>
                      <td className="small">
                        {t.estado === 'running' ? <Pill tone="ok">En marcha</Pill> : t.hasta ? new Date(t.hasta).toLocaleTimeString('es-ES') : '—'}
                      </td>
                      <td className="mono">{hhmm(t.segundos)}</td>
                      <td className="small">{t.causa ? <Pill tone="warn">{t.causa}</Pill> : '—'}</td>
                      <td className="small muted">{t.origen === 'historico' ? 'terminal clásico' : t.origen === 'demo' ? 'demostración' : 'terminal'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : null}

        {pestana === 'componentes' ? (
          <div style={{ overflowX: 'auto' }}>
            <table className="table table--zebra">
              <thead>
                <tr><th>Artículo</th><th>Fase</th><th>Por pieza</th><th>Necesario</th><th>Ya emitido</th><th>Almacén</th><th>Gestión</th><th>Método</th></tr>
              </thead>
              <tbody>
                {componentes.map((l) => (
                  <tr key={l.lineNum}>
                    <td>
                      {l.description}
                      <div className="small muted mono">{l.itemCode}</div>
                    </td>
                    <td className="small">{l.phaseCode ?? '—'}</td>
                    <td className="mono">{qty(l.baseQuantity)}</td>
                    <td className="mono">{qty(l.plannedQuantity)} {l.uom ?? ''}</td>
                    <td className="mono">{qty(l.issuedQuantity)}</td>
                    <td className="small mono">{l.warehouse ?? o.warehouse}</td>
                    <td className="small">
                      {l.managedBy === 'batch' ? 'Lotes' : l.managedBy === 'serial' ? 'Series' : '—'}
                      {l.binEnabled ? ' · ubicaciones' : ''}
                    </td>
                    <td className="small muted">{l.issueMethod === 'manual' ? 'Manual' : 'Automático'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="eyebrow" style={{ marginTop: 18 }}>Recursos de la ruta</div>
            <table className="table table--zebra">
              <thead><tr><th>Recurso</th><th>Fase</th><th>Por pieza</th><th>Previsto total</th><th>Ya emitido</th></tr></thead>
              <tbody>
                {recursos.map((l) => (
                  <tr key={l.lineNum}>
                    <td className="mono">{l.itemCode}</td>
                    <td className="small">{l.phaseCode ?? '—'}</td>
                    <td className="mono">{qty(l.baseQuantity)}</td>
                    <td className="mono">{qty(l.plannedQuantity)}</td>
                    <td className="mono">{qty(l.issuedQuantity)}</td>
                  </tr>
                ))}
                {recursos.length === 0 ? (
                  <tr><td colSpan={5} className="muted small">La OF no tiene líneas de recurso: su ruta no lleva tiempos.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        ) : null}

        {pestana === 'sap' ? (
          <div style={{ overflowX: 'auto' }}>
            <div className="eyebrow">Producción recibida</div>
            {datos.recibos.length === 0 ? (
              <p className="muted small">Todavía no se ha recibido ninguna producción de esta orden.</p>
            ) : (
              <table className="table table--zebra">
                <thead><tr><th>Cantidad</th><th>Rechazo</th><th>Quién</th><th>Cuándo</th><th>Documento SAP</th></tr></thead>
                <tbody>
                  {datos.recibos.map((r, i) => (
                    <tr key={i}>
                      <td className="mono">{qty(r.cantidad)}</td>
                      <td className="mono">{r.rechazo > 0 ? qty(r.rechazo) : '—'}</td>
                      <td>{r.por ?? <span className="muted small">{r.origen === 'historico' ? 'terminal clásico' : '—'}</span>}</td>
                      <td className="small">{new Date(r.cuando).toLocaleString('es-ES')}</td>
                      <td className="mono small">{r.sapDocNum ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="eyebrow" style={{ marginTop: 18 }}>Documentos enviados a SAP</div>
            {datos.documentos.length === 0 ? (
              <p className="muted small">
                No se ha mandado ningún documento desde el terminal para esta orden.
              </p>
            ) : (
              <table className="table table--zebra">
                <thead><tr><th>Tipo</th><th>Resumen</th><th>Estado</th><th>Nº en SAP</th><th>Cuándo</th></tr></thead>
                <tbody>
                  {datos.documentos.map((d, i) => (
                    <tr key={i}>
                      <td className="small">{tipoDocumento(d.tipo)}</td>
                      <td className="small">
                        {d.resumen}
                        {d.error ? <div className="small" style={{ color: 'var(--danger)' }}>{d.error}</div> : null}
                      </td>
                      <td>
                        {d.estado === 'done' ? <Pill tone="ok">Contabilizado</Pill>
                          : d.estado === 'failed' ? <Pill tone="danger">Error · {d.intentos} intentos</Pill>
                          : <Pill tone="warn">En cola</Pill>}
                      </td>
                      <td className="mono small">{d.sapDocNum ?? '—'}</td>
                      <td className="small">{new Date(d.cuando).toLocaleString('es-ES')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

function DatoFicha({ etiqueta, valor, sub }: { etiqueta: string; valor: string; sub?: string }) {
  return (
    <div className="kpi__card">
      <div className="kpi__label">{etiqueta}</div>
      <div className="kpi__value" style={{ fontSize: 26 }}>{valor}</div>
      {sub ? <div className="small muted" style={{ marginTop: 6 }}>{sub}</div> : null}
    </div>
  );
}

function estadoLegible(status: string): string {
  return status === 'released' ? 'Liberada'
    : status === 'planned' ? 'Planificada'
    : status === 'closed' ? 'Cerrada'
    : 'Cancelada';
}

function tipoDocumento(kind: string): string {
  return kind === 'phase.issue' ? 'Emisión de fase'
    : kind === 'order.receipt' ? 'Recibo de producción'
    : kind === 'order.close' ? 'Cierre de la OF'
    : kind === 'reservation.transfer' ? 'Reserva de material'
    : kind;
}

/**
 * Lo que ha encontrado el buscador, agrupado por lo que es cada cosa.
 *
 * Se enseñan los grupos que tienen algo y se callan los vacíos: una pantalla
 * con cinco cabeceras y cuatro «sin resultados» obliga a leerlo todo para
 * descubrir dónde está lo único que hay.
 */
function Resultados({
  consulta,
  datos,
  onAbrirOrden,
}: {
  consulta: string;
  datos: Busqueda | null;
  onAbrirOrden: (docEntry: number) => void;
}) {
  if (!datos) return <Empty icon="🔎" title={`Buscando «${consulta}»…`} />;

  const total =
    datos.ordenes.length + datos.personas.length + datos.fases.length + datos.causas.length + datos.documentos.length;

  if (total === 0) {
    return (
      <Empty
        icon="🔎"
        title={`Nada con «${consulta}»`}
        body="Se ha buscado en órdenes (también cerradas), personas, fases, causas de parada y documentos enviados a SAP, incluidos los lotes y series que llevan dentro."
      />
    );
  }

  return (
    <div className="stack">
      <div className="eyebrow">
        {total} {total === 1 ? 'resultado' : 'resultados'} para «{consulta}»
      </div>

      {datos.ordenes.length > 0 ? (
        <section className="card stack">
          <div className="eyebrow">Órdenes de fabricación</div>
          <div style={{ overflowX: 'auto' }}>
            <table className="table table--zebra">
              <thead>
                <tr><th>OF</th><th>Artículo</th><th>Estado</th><th>Piezas</th><th>Entrega</th><th /></tr>
              </thead>
              <tbody>
                {datos.ordenes.map((o) => (
                  <tr key={o.docEntry}>
                    <td><strong>{o.docNum}</strong></td>
                    <td>
                      {o.itemDescription}
                      <div className="small muted mono">
                        {o.itemCode}
                        {o.customerName ? ` · ${o.customerName}` : ''}
                        {o.project ? ` · ${o.project}` : ''}
                      </div>
                    </td>
                    <td>
                      {o.status === 'released' ? <Pill tone="ok">Liberada</Pill>
                        : o.status === 'planned' ? <Pill tone="warn">Planificada</Pill>
                        : <Pill>Cerrada</Pill>}
                    </td>
                    <td className="mono">{qty(o.completedQuantity)}/{qty(o.plannedQuantity)}</td>
                    <td className="small">{shortDate(o.dueDate)}</td>
                    <td>
                      <button className="btn" onClick={() => onAbrirOrden(o.docEntry)}>Ver ficha</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {datos.personas.length > 0 ? (
        <section className="card stack">
          <div className="eyebrow">Personas</div>
          <table className="table table--zebra">
            <thead><tr><th>Nombre</th><th>Nº</th><th>Puesto</th><th>Recursos</th><th>Hoy</th></tr></thead>
            <tbody>
              {datos.personas.map((p) => (
                <tr key={p.employeeId}>
                  <td>
                    {p.nombre}
                    {p.trabajandoAhora ? <> <Pill tone="ok">Trabajando ahora</Pill></> : null}
                  </td>
                  <td className="mono">{p.employeeId}</td>
                  <td className="small muted">{p.puesto ?? '—'}</td>
                  <td className="small mono">{p.recursos.join(', ') || <span className="muted">ninguno</span>}</td>
                  <td className="mono">{p.segundosHoy > 0 ? hhmm(p.segundosHoy) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {datos.fases.length > 0 ? (
        <section className="card stack">
          <div className="eyebrow">Fases</div>
          <table className="table table--zebra">
            <thead><tr><th>Código</th><th>Nombre</th><th>Tiempo imputado</th><th>En cuántas OFs</th></tr></thead>
            <tbody>
              {datos.fases.map((f) => (
                <tr key={f.code}>
                  <td className="mono"><strong>{f.code}</strong></td>
                  <td>{f.name}</td>
                  <td className="mono">{hhmm(f.segundos)}</td>
                  <td className="mono">{f.ofs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {datos.causas.length > 0 ? (
        <section className="card stack">
          <div className="eyebrow">Causas de parada</div>
          <table className="table table--zebra">
            <thead><tr><th>Código</th><th>Nombre</th><th>Tipo</th><th>Veces</th><th>Tiempo</th></tr></thead>
            <tbody>
              {datos.causas.map((c) => (
                <tr key={c.code}>
                  <td className="mono"><strong>{c.code}</strong></td>
                  <td>{c.name}</td>
                  <td className="small muted">{c.tipo === 'fase' ? 'Parada de fase' : 'Salida de presencia'}</td>
                  <td className="mono">{c.veces}</td>
                  <td className="mono">{hhmm(c.segundos)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {datos.documentos.length > 0 ? (
        <section className="card stack">
          <div className="eyebrow">Documentos enviados a SAP</div>
          <p className="muted small" style={{ margin: 0 }}>
            Se busca también dentro del documento, así que un lote o un número de serie encuentra la emisión en la que
            se consumió.
          </p>
          <table className="table table--zebra">
            <thead><tr><th>Tipo</th><th>Resumen</th><th>Estado</th><th>Nº en SAP</th><th>Cuándo</th><th /></tr></thead>
            <tbody>
              {datos.documentos.map((d, i) => (
                <tr key={i}>
                  <td className="small">{tipoDocumento(d.tipo)}</td>
                  <td className="small">
                    {d.resumen}
                    {d.error ? <div className="small" style={{ color: 'var(--danger)' }}>{d.error}</div> : null}
                  </td>
                  <td>
                    {d.estado === 'done' ? <Pill tone="ok">Contabilizado</Pill>
                      : d.estado === 'failed' ? <Pill tone="danger">Error</Pill>
                      : <Pill tone="warn">En cola</Pill>}
                  </td>
                  <td className="mono small">{d.sapDocNum ?? '—'}</td>
                  <td className="small">{new Date(d.cuando).toLocaleString('es-ES')}</td>
                  <td>
                    {d.docEntry ? (
                      <button className="btn" onClick={() => onAbrirOrden(d.docEntry as number)}>Ver OF</button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </div>
  );
}
